import { useQuery } from '@tanstack/react-query'

import client, { Schemas } from '~/clients/api-client'
import { useFlag } from '~/hooks/useFlag'
import { SurgeryTypeGroup, useGetResolvedSurgeryTypeGroup, useOrderedLocations, usePractitioners } from '~/storm'
import { capitalizeWords } from '~/utils/dataFormatters'
import { getPractitionerFullname } from '~/utils/dips'
import { getInitials } from '~/utils/user'

import { MentionItem, MentionType } from './lexical/mentions/types'

const simpleMatch = (value: string, query: string) => value.toLowerCase().includes(query.toLowerCase())

const userToMention = (user: Schemas['UserOut']): MentionItem => ({ id: user.id.toString(), value: capitalizeWords(user.name ?? ''), type: 'user' })

const surgeryTypeToMention = (surgeryType: SurgeryTypeGroup): MentionItem => ({
    id: surgeryType.id.toString(),
    value: surgeryType.display_name,
    type: 'surgeryType',
})

type MentionItemGroup = {
    entityType: MentionType
    label: string
    options: MentionItem[]
}

export const useMentions = (query: string | null): MentionItemGroup[] => {
    const practitioners = usePractitioners({ include: { dipsResource: true } })
    const locations = useOrderedLocations()
    const getSurgeryTypes = useGetResolvedSurgeryTypeGroup()
    const userMentionsEnabled = useFlag('comments_v2__user_mentions_enabled')
    const surgeryTypeMentionsEnabled = useFlag('comments_v2__surgery_type_mentions_enabled')

    const { data: users } = useQuery({
        queryKey: ['users'],
        queryFn: () => client.fetchUsers({}),
        staleTime: Infinity,
        select: data => data.data?.filter(user => !user.is_hidden && !!user.name).map(userToMention),
        enabled: userMentionsEnabled,
    })

    if (query === null) {
        return []
    }

    const surgeryTypes = surgeryTypeMentionsEnabled
        ? getSurgeryTypes
              .search(query)
              .filter(s => !s.hidden)
              .map(surgeryTypeToMention)
        : []

    const practitionerMatches = practitioners
        .map(p => ({ id: p.id.toString(), value: p.short_name, type: 'practitioner', fullname: getPractitionerFullname(p) }) as const)
        .filter(m => simpleMatch(m.value, query) || simpleMatch(m.fullname, query))

    const userMentions = users?.filter(user => simpleMatch(user.value, query) || simpleMatch(getInitials(user.value), query)) ?? []

    const locationMatches = locations
        .filter(location => simpleMatch(location.room_code, query))
        .map(l => ({ id: l.id.toString(), value: l.room_code, type: 'location' }) as const)

    const groups: MentionItemGroup[] = [
        {
            entityType: 'user',
            label: 'Brukere',
            options: userMentions,
        },
        {
            entityType: 'surgeryType',
            label: 'Operasjonskategorier',
            options: surgeryTypes,
        },
        {
            entityType: 'practitioner',
            label: 'Kirurger',
            options: practitionerMatches,
        },
        {
            entityType: 'location',
            label: 'Rom',
            options: locationMatches,
        },
    ]

    return groups
}
